import { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  BarElement, Tooltip, Legend,
} from 'chart.js';
import { useDashboard } from '../../hooks/useDashboard';
import { useCache } from '../../hooks/useCache';
import { fetchFootfallDaily } from '../../api';
import { ChartSkeleton } from '../ui/Skeleton';
import EmptyState from '../ui/EmptyState';
import styles from './Charts.module.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseISO(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function fmtFull(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return `${String(d).padStart(2, '0')} ${MONTHS[m - 1]}, ${y}`;
}

function fmtDayMonth(d) {
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]}`;
}

function fmtISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildDateWindow(mode, endISO) {
  if (mode === 'day') return { fromDate: endISO, toDate: endISO };
  const days = mode === 'week' ? 7 : 30;
  const end = parseISO(endISO);
  const start = new Date(end);
  start.setDate(end.getDate() - (days - 1));
  return { fromDate: fmtISO(start), toDate: fmtISO(end) };
}

export default function FootfallTrend() {
  const { currentStore, visibleStores, endDate } = useDashboard();
  const [mode, setMode] = useState('week');

  const storeIds = currentStore
    ? currentStore.storeId
    : visibleStores.map(s => s.storeId).join(',');

  const window = buildDateWindow(mode, endDate);

  const cacheKey = storeIds && window.fromDate && window.toDate
    ? `footfall-daily:${storeIds}:${window.fromDate}:${window.toDate}`
    : null;
  const { data, loading } = useCache(cacheKey, () => fetchFootfallDaily(storeIds, window.fromDate, window.toDate));

  const dailyData = data?.dailyData || [];
  const labels = dailyData.map(d => fmtDayMonth(parseISO(d.date)));
  const counts = dailyData.map(d => d.count || 0);

  const sub = window.fromDate && window.toDate
    ? `Based on ${fmtFull(window.fromDate)} – ${fmtFull(window.toDate)}`
    : '';

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Footfall',
        data: counts,
        backgroundColor: '#7ec8e3',
        borderRadius: 0,
        maxBarThickness: 60,
      },
    ],
  };

  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: { label: item => ` Footfall: ${item.raw.toLocaleString()}` },
        backgroundColor: '#1f2937', titleColor: '#f9fafb',
        bodyColor: '#d1d5db', padding: 8, cornerRadius: 6,
      },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#667085', font: { size: 11 } } },
      y: {
        grid: { color: 'rgba(0,0,0,0.04)', drawTicks: false },
        border: { display: false },
        ticks: { color: '#667085', font: { size: 11 }, callback: v => v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v },
      },
    },
  };

  const hasData = dailyData.length > 0;

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div>
          <div className={styles.title}>Footfall Trend</div>
          <div className={styles.sub}>{sub}</div>
        </div>
        <div className={styles.toggle}>
          {['day', 'week', 'month'].map(m => (
            <button
              key={m}
              type="button"
              className={`${styles.toggleBtn} ${mode === m ? styles.toggleBtnActive : ''}`}
              onClick={() => setMode(m)}
            >
              {m.charAt(0).toUpperCase() + m.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <ChartSkeleton height={200} />
      ) : !hasData ? (
        <EmptyState message="No footfall data available" />
      ) : (
        <div className={styles.chartWrap}>
          <Bar data={chartData} options={options} />
        </div>
      )}
    </div>
  );
}
